import { App } from "antd";
import { useEffect } from "react";
import { useFetchData } from "../../../hooks/useFetch/post";
import { useFetchData as usePagination } from "../../../hooks/useFetch/pagination";
import type {
  RemappingHistoryDataType,
  RemappingUploadResponseType,
} from "./RemappingType";
import { useFetchDownload } from "../../../hooks/useFetch/download";

const useOnRequestError = (error, clearError, messageApi) => {
  useEffect(() => {
    if (!error) return;
    messageApi.error(error.message);
    clearError?.();
  }, [messageApi, error]);
};

export const useRemapping = ({ selectedBatch, selectedFile, pageSize }) => {
  const { message: messageApi } = App.useApp();

  const [
    fetchRemappingUploadResponse,
    {
      data: remappingUploadResponse,
      isLoading: isUploading,
      error: remappingUploadResponseError,
      clearError: clearRemappinUploadResponseError,
      clearData: clearRemappingUploadResponse,
    },
  ] = useFetchData<RemappingUploadResponseType>({
    optionalArray: [selectedFile, selectedBatch],
    query: "/@batch/remapping/upload",
    selectData: (data) => data,
  });

  useOnRequestError(
    remappingUploadResponseError,
    clearRemappinUploadResponseError,
    messageApi,
  );

  const [
    fetchRemappingJob,
    {
      data: remappingJobData,
      isLoading: isJobLoading,
      error: remappingJobDataError,
      clearError: clearRemappingJobDataError,
      clearData: clearRemappingJobData,
    },
  ] = useFetchData<{ success: boolean }>({
    optionalArray: [selectedFile, selectedBatch],
    query: "/@batch/remapping/add",
    selectData: (data) => data,
  });

  useOnRequestError(
    remappingJobDataError,
    clearRemappingJobDataError,
    messageApi,
  );

  const [
    fetchRemappingHistory,
    {
      data: remappingHistoryData,
      page,
      totalRows,
      isLoading: isHistoryLoading,
      error: remappingHistoryDataError,
      loadMore,
      clearError: clearRemappingHistoryDataError,
    },
  ] = usePagination<RemappingHistoryDataType[]>({
    query: "/remapping",
    perPage: pageSize,
    selectData: (data) => data.items,
    selectPageInfo: (data) => data.page_info,
  });

  useOnRequestError(
    remappingHistoryDataError,
    clearRemappingHistoryDataError,
    messageApi,
  );

  useEffect(() => {
    fetchRemappingHistory({}, { filters: {} });
  }, []);

  useEffect(() => {
    if (remappingJobData?.success) {
      messageApi.success("Remapping job has been started!");
      fetchRemappingHistory({}, { filters: {} });
    }
  }, [remappingJobData?.success, messageApi]);

  const [
    fetchRemappingPreview,
    {
      data: remappingPreviewUrl,
      isLoading: isRemappingPreviewLoading,
      error: remappingPreviewDataError,
      clearError: clearRemappingPreviewDataError,
      clearData: clearRemappingPreviewData,
    },
  ] = useFetchDownload({
    optionalArray: [selectedFile, selectedBatch],
    query: "/@batch/remapping/preview",
  });

  useOnRequestError(
    remappingPreviewDataError,
    clearRemappingPreviewDataError,
    messageApi,
  );

  useEffect(() => {
    if (remappingPreviewUrl?.download_url) {
      window.location.href = remappingPreviewUrl.download_url;
    }
  }, [remappingPreviewUrl]);

  const clearWorkflowState = () => {
    clearRemappingUploadResponse();
    clearRemappingJobData();
    clearRemappingPreviewData();
  };

  return {
    fetchRemappingUploadResponse,
    remappingUploadResponse,
    isUploading,
    clearRemappingUploadResponse,
    clearWorkflowState,

    remappingJob: () =>
      fetchRemappingJob(
        { batch: selectedBatch },
        {
          remapping_sheet_id: remappingUploadResponse?.remapping_sheet_id,
        },
      ),
    isJobLoading,
    remappingJobData,

    remappingPreview: () =>
      fetchRemappingPreview(
        { batch: selectedBatch },
        {
          remapping_sheet_id: remappingUploadResponse?.remapping_sheet_id,
        },
      ),
    isRemappingPreviewLoading,

    fetchRemappingHistory,
    remappingHistoryData,
    page,
    totalRows,
    isHistoryLoading,
    loadMore,

    remappingPreviewUrl,
  };
};
